import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { m } from "framer-motion";
import { Icon } from "@/components/Icon";
import {
  AnonymousCaptureError,
  captureUrl,
  clearSavedCapture,
  getIntentSession,
  linkIsPublishable,
  linkIsTerminalFailure,
  loadSavedCapture,
  normalizeCaptureUrl,
  publishIntentSession,
  removeSessionLink,
  saveCapture,
  signupPromptCopy,
  type CapturePhase,
  type PublishedClip,
  type SessionDetail,
  type SessionLink,
  type SignupPrompt,
} from "@/lib/anonymous-capture";
import { track } from "@/lib/analytics";
import TurnstileChallenge from "@/components/landing/TurnstileChallenge";
import {
  isChallengeRecoverable,
  setChallengeToken,
} from "@/lib/anonymous-capture";
import { turnstileEnabled, type TurnstileStatus } from "@/lib/turnstile";
import { clerkSignUpUrl } from "@/lib/auth/clerk";
import { handoffUrlFor } from "@/lib/auth/handoff";

const POLL_INTERVAL_MS = 1_800;
const POLL_TIMEOUT_MS = 120_000;
const ANONYMOUS_LINK_LIMIT = 3;

const delay = (milliseconds: number, signal: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }
    const timeout = window.setTimeout(resolve, milliseconds);
    signal.addEventListener(
      "abort",
      () => {
        window.clearTimeout(timeout);
        reject(new DOMException("Aborted", "AbortError"));
      },
      { once: true },
    );
  });

function domainFor(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

function sourceLabel(kind: string): string {
  return kind.replace(/_/g, " ");
}

function statusLabel(link: SessionLink): string {
  if (link.status === "ready") return "Ready";
  if (link.status === "partial") return "Ready with a basic preview";
  if (link.status === "failed") return "Could not process";
  if (link.status === "rejected") return "Not supported";
  return "Understanding";
}

function ThreadCard({
  detail,
  phase,
  removing = false,
  onRemoveLink = null,
}: {
  detail: SessionDetail | null;
  phase: CapturePhase;
  removing?: boolean;
  onRemoveLink?: ((link: SessionLink) => void) | null;
}) {
  const working = phase === "submitting" || phase === "processing" || phase === "publishing" || removing;
  const needsAttention = phase === "error";

  if (!detail) {
    return (
      <div className="flex min-h-[360px] flex-col justify-between rounded-[24px] border border-white/[0.08] bg-black/20 p-6 md:p-8">
        <div className="flex items-center justify-between gap-4">
          <span className="ios-chip-label text-ios-muted">Your thread</span>
          <span className="rounded-full border border-white/[0.08] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-ios-muted">
            Private while building
          </span>
        </div>
        <div className="py-10">
          <div className="mb-5 grid h-12 w-12 place-items-center rounded-2xl bg-ios-accent/15 text-ios-accent-text">
            <Icon name="link" />
          </div>
          <h3 className="max-w-sm text-2xl font-bold tracking-[-0.035em] text-ios">
            Your source becomes a working thread here.
          </h3>
          <p className="mt-3 max-w-md text-sm leading-relaxed text-ios-secondary">
            ManticsCore reads the public page, builds the source card, then returns a link that opens with no install.
          </p>
        </div>
        <div className="grid grid-cols-3 gap-2 border-t border-white/[0.08] pt-4 text-[11px] text-ios-muted">
          <span>1. Paste</span>
          <span>2. Understand</span>
          <span>3. Share</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[360px] rounded-[24px] border border-white/[0.08] bg-black/20 p-5 md:p-7">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-white/[0.08] pb-5">
        <div>
          <p className="ios-chip-label text-ios-muted">Live thread</p>
          <h3 className="mt-2 max-w-md text-xl font-bold tracking-[-0.025em] text-ios md:text-2xl">
            {detail.session.title || "Building your thread"}
          </h3>
          {detail.session.intent_summary ? (
            <p className="mt-2 max-w-lg text-sm leading-relaxed text-ios-secondary">
              {detail.session.intent_summary}
            </p>
          ) : null}
        </div>
        <span className="inline-flex items-center gap-2 rounded-full border border-white/[0.08] bg-white/[0.035] px-3 py-1.5 text-[11px] font-semibold text-ios-secondary">
          <span className={`h-1.5 w-1.5 rounded-full ${working ? "animate-pulse bg-ios-accent" : needsAttention ? "bg-red-300" : "bg-[rgb(var(--ios-success))]"}`} />
          {working ? "Working" : needsAttention ? "Needs attention" : "Ready"}
        </span>
      </div>

      <div className="space-y-3 pt-5">
        {detail.links.map((link) => {
          const publishable = linkIsPublishable(link);
          const failed = linkIsTerminalFailure(link);
          return (
            <div
              key={link.id}
              className="group flex items-start gap-3 rounded-2xl border border-white/[0.07] bg-white/[0.035] p-4 transition-colors hover:border-white/[0.14] hover:bg-white/[0.055]"
            >
              <a
                href={link.url_canonical}
                target="_blank"
                rel="noreferrer"
                className="flex min-w-0 flex-1 items-start gap-3"
              >
                <span className={`mt-0.5 grid h-10 w-10 shrink-0 place-items-center rounded-xl ${failed ? "bg-red-500/10 text-red-300" : "bg-ios-accent/15 text-ios-accent-text"}`}>
                  <Icon name={publishable ? "check_circle" : failed ? "warning" : "progress_activity"} size="sm" className={!publishable && !failed ? "animate-spin" : ""} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block break-words text-sm font-semibold leading-snug text-ios">
                    {link.title || domainFor(link.url_canonical)}
                  </span>
                  {link.summary ? (
                    <span className="mt-1.5 block break-words text-xs leading-relaxed text-ios-secondary">
                      {link.summary}
                    </span>
                  ) : null}
                  <span className="mt-2 flex flex-wrap gap-x-2 gap-y-1 text-[10px] uppercase tracking-[0.07em] text-ios-muted">
                    <span>{domainFor(link.url_canonical)}</span>
                    <span aria-hidden="true">·</span>
                    <span>{sourceLabel(link.source_kind)}</span>
                    <span aria-hidden="true">·</span>
                    <span>{statusLabel(link)}</span>
                  </span>
                </span>
                <Icon name="open_in_new" size="sm" className="mt-1 shrink-0 text-ios-muted transition-colors group-hover:text-ios" />
              </a>
              {onRemoveLink ? (
                <button
                  type="button"
                  onClick={() => onRemoveLink(link)}
                  disabled={working}
                  aria-label={`Remove ${link.title || domainFor(link.url_canonical)}`}
                  className="grid min-h-11 w-11 shrink-0 place-items-center rounded-xl text-ios-muted transition-colors hover:bg-white/[0.07] hover:text-ios disabled:cursor-not-allowed disabled:opacity-45"
                >
                  <Icon name="close" size="sm" />
                </button>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

async function waitForLink(
  sessionId: string,
  linkId: string,
  signal: AbortSignal,
  onUpdate: (detail: SessionDetail) => void,
): Promise<SessionDetail> {
  const startedAt = Date.now();

  while (Date.now() - startedAt < POLL_TIMEOUT_MS) {
    const detail = await getIntentSession(sessionId, signal);
    onUpdate(detail);
    const link = detail.links.find((candidate) => candidate.id === linkId);
    if (linkIsPublishable(link)) return detail;
    if (linkIsTerminalFailure(link)) {
      throw new AnonymousCaptureError("That source could not be processed. Try another public URL.");
    }
    await delay(POLL_INTERVAL_MS, signal);
  }

  throw new AnonymousCaptureError("This source is taking longer than expected. You can try again in a moment.");
}

export default function AnonymousCapture() {
  const [initialSaved] = useState(loadSavedCapture);
  const saved = useRef(initialSaved);
  const abortRef = useRef<AbortController | null>(null);
  const [url, setUrl] = useState("");
  const [phase, setPhase] = useState<CapturePhase>(saved.current ? "ready" : "idle");
  const [sessionId, setSessionId] = useState<string | null>(saved.current?.sessionId ?? null);
  const [detail, setDetail] = useState<SessionDetail | null>(null);
  const [clip, setClip] = useState<PublishedClip | null>(
    saved.current
      ? { slug: saved.current.slug, clip_url: saved.current.clipUrl, published_at: new Date(saved.current.updatedAt).toISOString() }
      : null,
  );
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [quotaLimited, setQuotaLimited] = useState(false);
  const [removingLinkId, setRemovingLinkId] = useState<string | null>(null);
  // backend#354, additive on the response. Never persisted with the saved
  // capture: it is per-response, not a property of the thread, so a reload
  // correctly shows no CTA until the next capture in this tab.
  const [signupPrompt, setSignupPrompt] = useState<SignupPrompt | null>(null);
  // BK-153-1 proof-of-human. `unavailable` is a deliberate fail-open: the
  // submit button stays enabled and the backend applies the stricter
  // unverified-tier limit. See src/lib/turnstile.ts.
  const [challengeStatus, setChallengeStatus] = useState<TurnstileStatus>(
    turnstileEnabled() ? "loading" : "disabled",
  );
  const [challengeReset, setChallengeReset] = useState(0);
  const [challengeRetryable, setChallengeRetryable] = useState(false);
  const challengeBlocking =
    challengeStatus === "loading" || challengeStatus === "ready" || challengeStatus === "expired";
  const acceptedLinkCount = detail?.links.filter((link) => !linkIsTerminalFailure(link)).length ?? 0;
  const atAnonymousLimit = quotaLimited || acceptedLinkCount >= ANONYMOUS_LINK_LIMIT;

  useEffect(() => {
    if (!saved.current) return;
    const controller = new AbortController();
    getIntentSession(saved.current.sessionId, controller.signal)
      .then(setDetail)
      .catch((cause) => {
        if (cause instanceof DOMException && cause.name === "AbortError") return;
        clearSavedCapture();
        setSessionId(null);
        setClip(null);
        setPhase("idle");
      });
    return () => controller.abort();
  }, []);

  useEffect(() => () => abortRef.current?.abort(), []);

  /**
   * Queues a fresh challenge. A Turnstile token is single-use and the backend
   * refuses a replay, so every capture attempt that carried one leaves us
   * holding a spent token; without this the *next* capture would silently go
   * out unverified and land on the stricter tier.
   */
  const refreshChallenge = useCallback(() => {
    setChallengeToken(null);
    setChallengeReset((count) => count + 1);
  }, []);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (atAnonymousLimit) {
      setError("This browser has reached its 3-link limit for today.");
      return;
    }
    if (challengeBlocking) {
      setError(
        challengeStatus === "expired"
          ? "The human check expired. Complete it again to continue."
          : "Complete the human check to continue.",
      );
      return;
    }
    const normalized = normalizeCaptureUrl(url);
    if (!normalized) {
      setError("Paste a complete public URL and try again.");
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setError(null);
    setCopied(false);
    setPhase("submitting");
    setChallengeRetryable(false);
    track("web_capture_submitted", {
      has_existing_session: Boolean(sessionId),
      challenge_status: challengeStatus,
    });
    if (challengeStatus === "unavailable") {
      // Fail-open is tracked so a spike is visible rather than silent.
      track("turnstile_unavailable", { stage: "capture_submit" });
    }

    try {
      const captured = await captureUrl(normalized, sessionId, controller.signal);
      // That request spent the challenge. Get another one in flight now so a
      // second capture is verified too, rather than quietly unverified.
      if (turnstileEnabled()) refreshChallenge();
      // The block belongs to this capture succeeding, not to the thread
      // ending up ready — set it now and keep it even if the poll below
      // fails, since /v1/shares already returned it.
      setSignupPrompt(captured.signup_prompt ?? null);
      setSessionId(captured.session_id);
      setUrl("");
      setPhase("processing");

      const readyDetail = await waitForLink(
        captured.session_id,
        captured.link_id,
        controller.signal,
        setDetail,
      );
      setDetail(readyDetail);
      setPhase("captured");
    } catch (cause) {
      if (cause instanceof DOMException && cause.name === "AbortError") return;
      setPhase("error");
      const message = cause instanceof AnonymousCaptureError
        ? cause.message
        : "ManticsCore could not be reached. Check your connection and try again.";
      if (cause instanceof AnonymousCaptureError && cause.code === "anonymous_link_limit") {
        setQuotaLimited(true);
      }
      if (isChallengeRecoverable(cause)) {
        // A Turnstile token is single-use: never retry with the stale one.
        refreshChallenge();
        setChallengeRetryable(true);
        track("turnstile_challenge_retry", {
          code: cause instanceof AnonymousCaptureError ? cause.code : null,
          status: cause instanceof AnonymousCaptureError ? cause.status : 0,
        });
      } else if (turnstileEnabled()) {
        // Not a challenge failure, but the token still rode the failed
        // request and is spent. Replace it so a retry is not downgraded.
        refreshChallenge();
      }
      setError(message);
      track("web_capture_failed", {
        status: cause instanceof AnonymousCaptureError ? cause.status : 0,
        code: cause instanceof AnonymousCaptureError ? cause.code : null,
      });
    }
  };

  const publishThread = async () => {
    if (!sessionId || !detail?.links.some(linkIsPublishable)) return;
    const retried = phase === "error";

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setError(null);
    setCopied(false);
    setPhase("publishing");
    if (retried) track("web_capture_publish_retried", { session_id: sessionId });

    try {
      const published = await publishIntentSession(sessionId, controller.signal);
      setClip(published);
      setPhase("ready");
      saveCapture({
        sessionId,
        clipUrl: published.clip_url,
        slug: published.slug,
        updatedAt: Date.now(),
      });
      track("web_capture_published", {
        session_id: sessionId,
        source_count: detail.links.length,
        retried,
      });
    } catch (cause) {
      if (cause instanceof DOMException && cause.name === "AbortError") return;
      setPhase("error");
      const message = cause instanceof AnonymousCaptureError
        ? cause.message
        : "ManticsCore could not be reached. Check your connection and try again.";
      setError(message);
      track("web_capture_failed", {
        status: cause instanceof AnonymousCaptureError ? cause.status : 0,
        code: cause instanceof AnonymousCaptureError ? cause.code : null,
        retried,
      });
    }
  };

  const copyClip = async () => {
    if (!clip) return;
    try {
      await navigator.clipboard.writeText(clip.clip_url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2_000);
      track("web_capture_link_copied", { session_id: sessionId });
    } catch {
      setError("Copying was blocked by the browser. Open the link and copy it from the address bar.");
    }
  };

  const startFresh = () => {
    abortRef.current?.abort();
    clearSavedCapture();
    saved.current = null;
    setUrl("");
    setPhase("idle");
    setSessionId(null);
    setDetail(null);
    setClip(null);
    setError(null);
    setCopied(false);
    setSignupPrompt(null);
    track("web_capture_started_fresh");
  };

  const removeLink = async (link: SessionLink) => {
    if (!sessionId || removingLinkId) return;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setError(null);
    setCopied(false);
    setRemovingLinkId(link.id);

    try {
      await removeSessionLink(sessionId, link.id, controller.signal);

      // The server also adjusts link_count, session status, and may archive the thread.
      let refreshed: SessionDetail;
      try {
        refreshed = await getIntentSession(sessionId, controller.signal);
      } catch (cause) {
        if (cause instanceof DOMException && cause.name === "AbortError") return;
        // An archived thread may stop resolving. The removal already succeeded,
        // so recover instead of reporting a failure nobody can act on.
        if (cause instanceof AnonymousCaptureError && cause.status === 404) {
          track("web_capture_link_removed", { session_id: sessionId, remaining_sources: 0 });
          startFresh();
          return;
        }
        throw cause;
      }

      track("web_capture_link_removed", {
        session_id: sessionId,
        remaining_sources: refreshed.links.length,
      });

      if (refreshed.links.length === 0) {
        startFresh();
        return;
      }

      setDetail(refreshed);
      setPhase("captured");
    } catch (cause) {
      if (cause instanceof DOMException && cause.name === "AbortError") return;
      setPhase("error");
      const message = cause instanceof AnonymousCaptureError
        ? cause.message
        : "ManticsCore could not be reached. Check your connection and try again.";
      setError(message);
      track("web_capture_failed", {
        status: cause instanceof AnonymousCaptureError ? cause.status : 0,
        code: cause instanceof AnonymousCaptureError ? cause.code : null,
      });
    } finally {
      setRemovingLinkId(null);
    }
  };

  const working = phase === "submitting" || phase === "processing" || phase === "publishing" || Boolean(removingLinkId);
  const buttonLabel = atAnonymousLimit
    ? "3-link limit reached"
    : phase === "submitting"
      ? "Adding"
      : sessionId
        ? "Add source"
        : "Build the thread";
  const canRetryPublish = phase === "error" && Boolean(sessionId) && Boolean(detail?.links.some(linkIsPublishable));
  const signupPromptMessage = signupPromptCopy(signupPrompt);
  const signupUrl = clerkSignUpUrl(
    handoffUrlFor(typeof window !== "undefined" ? `${window.location.origin}/` : "https://manticscore.com/"),
  );

  return (
    <section id="try-it" className="scroll-mt-20 px-6 py-20 md:px-8 md:py-28" aria-labelledby="try-it-title">
      <m.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.12 }}
        transition={{ duration: 0.6 }}
        className="relative mx-auto max-w-6xl overflow-hidden rounded-[32px] border border-white/[0.09] bg-[#0d0d12] p-5 shadow-[0_36px_120px_rgba(10,76,145,0.14)] md:p-8 lg:p-10"
      >
        <div aria-hidden="true" className="pointer-events-none absolute -left-24 -top-32 h-96 w-96 rounded-full bg-ios-accent/15 blur-[100px]" />
        <div className="relative grid gap-8 lg:grid-cols-[0.82fr_1.18fr] lg:items-start lg:gap-10">
          <div className="lg:sticky lg:top-24">
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <span className="ios-chip-label text-ios-accent-text">Try the real product</span>
              <span className="rounded-full border border-white/[0.09] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-ios-muted">
                No account
              </span>
            </div>
            <h2 id="try-it-title" className="max-w-lg text-balance text-3xl font-bold tracking-[-0.045em] text-ios md:text-5xl">
              Paste a URL. Get a link worth sending.
            </h2>
            <p className="mt-5 max-w-lg text-base leading-relaxed text-ios-secondary">
              ManticsCore uses the same backend as the iPhone app to understand the source, build a thread, and publish a shareable App Clip URL.
            </p>

            <form onSubmit={submit} className="mt-7" noValidate>
              <label htmlFor="capture-url" className="mb-2 block text-xs font-semibold text-ios-secondary">
                Public URL
              </label>
              <div className="rounded-2xl border border-white/[0.1] bg-black/30 p-2 transition-colors focus-within:border-ios-accent/60">
                <input
                  id="capture-url"
                  type="url"
                  inputMode="url"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  value={url}
                  disabled={working || atAnonymousLimit}
                  onChange={(event) => setUrl(event.target.value)}
                  placeholder="github.com/openai/openai-agents-python"
                  className="min-h-12 w-full bg-transparent px-3 text-sm text-ios outline-none placeholder:text-ios-muted disabled:opacity-60"
                />
                <button
                  type="submit"
                  disabled={working || atAnonymousLimit || !url.trim()}
                  className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-xl bg-ios-accent px-5 text-sm font-semibold text-white transition-all hover:bg-ios-accent/90 disabled:cursor-not-allowed disabled:opacity-45"
                >
                  {working ? <Icon name="progress_activity" size="sm" className="animate-spin" /> : <Icon name={sessionId ? "add" : "arrow_forward"} size="sm" />}
                  {buttonLabel}
                </button>
              </div>
              <TurnstileChallenge
                onToken={setChallengeToken}
                onStatusChange={setChallengeStatus}
                resetSignal={challengeReset}
              />
            </form>

            <div aria-live="polite" className="mt-4 min-h-6">
              {phase === "processing" ? <p className="text-xs leading-relaxed text-ios-secondary">Reading the source and building its card. This usually takes a few seconds.</p> : null}
              {phase === "publishing" ? <p className="text-xs leading-relaxed text-ios-secondary">The thread is ready. Publishing its stable share URL now.</p> : null}
              {atAnonymousLimit && !error ? (
                <p className="text-xs leading-relaxed text-ios-secondary">
                  {clip
                    ? "3-link free limit reached. This thread is ready to share."
                    : "3-link free limit reached. Publish this thread when you are ready."}
                </p>
              ) : null}
              {error ? (
                <div className="flex flex-wrap items-center gap-3">
                  <p className="text-xs leading-relaxed text-red-300">{error}</p>
                  {challengeRetryable ? (
                    <p className="text-xs leading-relaxed text-ios-secondary">
                      A fresh human check is ready — submit again.
                    </p>
                  ) : null}
                  {canRetryPublish ? (
                    <button
                      type="button"
                      onClick={publishThread}
                      className="inline-flex min-h-9 items-center justify-center gap-1.5 rounded-lg border border-red-300/25 bg-red-300/[0.08] px-3 text-xs font-semibold text-red-200 transition-colors hover:bg-red-300/[0.14]"
                    >
                      <Icon name="refresh" size="sm" />
                      Try publishing again
                    </button>
                  ) : null}
                </div>
              ) : null}
              {!working && !error && !clip && !atAnonymousLimit && phase !== "captured" ? <p className="text-xs leading-relaxed text-ios-muted">Add up to 3 public links. Your thread stays private until you publish its unlisted URL.</p> : null}
            </div>

            {phase === "captured" ? (
              <div className="mt-5 rounded-2xl border border-white/[0.09] bg-white/[0.035] p-4">
                <p className="text-sm font-semibold text-ios">Your thread is ready and still private</p>
                <p className="mt-1 text-xs leading-relaxed text-ios-secondary">
                  Add another source, or publish now. Publishing creates a public unlisted URL you can send to anyone.
                </p>
                <button
                  type="button"
                  onClick={publishThread}
                  className="mt-4 inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl bg-ios-accent px-4 text-xs font-semibold text-white transition-colors hover:bg-ios-accent/90"
                >
                  <Icon name="ios_share" size="sm" />
                  Publish this thread
                </button>
              </div>
            ) : null}

            {clip ? (
              <div className="mt-5 rounded-2xl border border-[rgb(var(--ios-success)/0.28)] bg-[rgb(var(--ios-success)/0.07)] p-4">
                <div className="flex items-start gap-3">
                  <Icon name="check_circle" className="mt-0.5 shrink-0 text-[rgb(var(--ios-success))]" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-ios">Your App Clip link is live</p>
                    <a href={clip.clip_url} target="_blank" rel="noreferrer" className="mt-1 block break-all text-xs leading-relaxed text-ios-accent-text hover:text-white">
                      {clip.clip_url}
                    </a>
                  </div>
                </div>
                <div className="mt-4 grid gap-2 sm:grid-cols-2">
                  <button type="button" onClick={copyClip} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-white/[0.07] px-4 text-xs font-semibold text-ios transition-colors hover:bg-white/[0.11]">
                    <Icon name={copied ? "check" : "content_copy"} size="sm" />
                    {copied ? "Copied" : "Copy link"}
                  </button>
                  <a href={clip.clip_url} target="_blank" rel="noreferrer" onClick={() => track("web_capture_link_opened", { session_id: sessionId })} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-ios-accent px-4 text-xs font-semibold text-white transition-colors hover:bg-ios-accent/90">
                    Open App Clip
                    <Icon name="open_in_new" size="sm" />
                  </a>
                </div>
                {!atAnonymousLimit ? (
                  <button type="button" onClick={startFresh} className="mt-3 text-xs font-semibold text-ios-muted transition-colors hover:text-ios">
                    Start a fresh thread
                  </button>
                ) : null}
              </div>
            ) : null}

            {atAnonymousLimit ? (
              // The limit is the highest-intent moment in the funnel: someone
              // has used the product, liked it enough to come back, and hit a
              // wall. It had no designed state — a disabled button and an
              // error string, which reads as a malfunction rather than a
              // boundary.
              //
              // It is deliberately NOT gated on signupPromptMessage. That
              // block comes from the capture RESPONSE, and the wall is hit by
              // people whose request was refused, or who arrive already spent
              // and never make a request at all. Gating on it would leave the
              // exact case this exists for with nothing on screen.
              <div
                role="status"
                className="mt-5 rounded-2xl border border-ios-accent/25 bg-ios-accent/[0.07] p-4"
              >
                <p className="text-sm font-semibold text-ios">
                  That&rsquo;s today&rsquo;s free links used up
                </p>
                <p className="mt-1 text-xs leading-relaxed text-ios-secondary">
                  Anonymous threads are capped per browser per day. An account lifts the cap, keeps
                  what you have already built, and makes these threads yours across devices.
                </p>
                <a
                  href={signupUrl}
                  onClick={() =>
                    track("web_capture_limit_signup_clicked", { session_id: sessionId })
                  }
                  className="mt-4 inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl bg-ios-accent px-4 text-xs font-semibold text-white transition-colors hover:bg-ios-accent/90"
                >
                  Create an account
                  <Icon name="arrow_forward" size="sm" />
                </a>
                {/* Said plainly because the alternative — waiting — is a real
                    option, and hiding it would make the account feel like the
                    only way out of a trap. */}
                <p className="mt-2.5 text-[11px] text-ios-muted">
                  Or come back tomorrow — the cap resets daily.
                </p>
              </div>
            ) : null}

            {signupPromptMessage && !atAnonymousLimit ? (
              // #117: rendered purely from the presence of the signup_prompt
              // response block (backend#354). Signed-in responses never carry
              // it, so absence means no CTA — there is no client-side gate.
              // The account-transfer half is already wired: AuthIsland calls
              // rebindAnonymousIfNeeded() after any sign-in, which POSTs
              // /auth/rebind with this browser's anon device_id.
              <div className="mt-5 rounded-2xl border border-ios-accent/25 bg-ios-accent/[0.07] p-4">
                <p className="text-sm font-semibold text-ios">Create an account to keep this thread</p>
                <p className="mt-1 text-xs leading-relaxed text-ios-secondary">{signupPromptMessage}</p>
                <a
                  href={signupUrl}
                  onClick={() => track("web_capture_signup_cta_clicked", { session_id: sessionId })}
                  className="mt-4 inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl bg-white/[0.07] px-4 text-xs font-semibold text-ios transition-colors hover:bg-white/[0.11]"
                >
                  Create an account
                  <Icon name="arrow_forward" size="sm" />
                </a>
              </div>
            ) : null}
          </div>

          <ThreadCard
            detail={detail}
            phase={phase}
            removing={Boolean(removingLinkId)}
            onRemoveLink={clip ? null : removeLink}
          />
        </div>
      </m.div>
    </section>
  );
}
